import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dataSourceService, SecuritySearchResult } from '../../services/dataSourceService';
import { assignmentService } from '../../services/assignmentService';
import { workflowService, WorkflowOption } from '../../services/workflowService';
import Button from '../ui/Button';

interface QuickStartModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * One-step ticker initiation — "you come in, you add your ticker, boom."
 * Type a ticker, pick a workflow (defaults to the earnings checklist), and
 * land straight in the task. On a duplicate the backend's 409 carries the
 * existing assignment id, so we offer "Resume workup" instead of a dead end.
 */
const QuickStartModal: React.FC<QuickStartModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SecuritySearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<{ uid: string; name: string } | null>(null);

  const [workflows, setWorkflows] = useState<WorkflowOption[]>([]);
  const [workflowId, setWorkflowId] = useState('');
  const [eventDate, setEventDate] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingId, setExistingId] = useState<string | null>(null);

  const reset = useCallback(() => {
    setQuery('');
    setResults([]);
    setSelected(null);
    setEventDate('');
    setError(null);
    setExistingId(null);
    setSubmitting(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    reset();
    workflowService
      .getAvailableWorkflows()
      .then(options => {
        setWorkflows(options);
        const earnings = options.find(w => /earnings preview/i.test(w.name));
        setWorkflowId((earnings ?? options[0])?.id ?? '');
      })
      .catch(() => setWorkflows([]));
  }, [isOpen, reset]);

  const handleSearch = async (q: string) => {
    setQuery(q);
    setSelected(null);
    setError(null);
    setExistingId(null);
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      setResults(await dataSourceService.searchSecurities(q, 8));
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const ticker = selected?.uid ?? query.trim().toUpperCase();
  const canSubmit = !!ticker && !!workflowId && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setExistingId(null);
    try {
      const assignment = await assignmentService.quickStart({
        ticker,
        name: selected?.name,
        workflowId,
        eventDate: eventDate || undefined,
      });
      onClose();
      navigate(`/task/${assignment.id}`);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      if (detail && typeof detail === 'object' && 'existingAssignmentId' in detail) {
        const d = detail as { message?: string; existingAssignmentId?: string };
        setError(d.message ?? 'An active workup already exists for this ticker.');
        setExistingId(d.existingAssignmentId ?? null);
      } else {
        setError(typeof detail === 'string' ? detail : 'Failed to start the workup.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-surface w-full max-w-md rounded-xl border border-neutral-200 shadow-xl p-6"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-neutral-900">Start a Workup</h2>
        <p className="text-sm text-neutral-500 mt-1 mb-4">
          Add a ticker and jump straight into the checklist.
        </p>

        {/* Ticker search */}
        <label className="block text-sm font-medium text-neutral-700 mb-1">Ticker</label>
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search by company name or ticker..."
            className="w-full h-9 px-3 text-sm bg-surface border border-neutral-200 rounded-lg transition-all"
            autoFocus
          />
          {searching && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        {results.length > 0 && !selected && (
          <div className="mt-2 border border-neutral-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-neutral-100">
            {results.map(sec => (
              <button
                key={sec.uid}
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-neutral-50 transition-colors"
                onClick={() => {
                  setSelected({ uid: sec.uid, name: sec.name });
                  setQuery(`${sec.uid} — ${sec.name}`);
                  setResults([]);
                }}
              >
                <span className="text-sm font-semibold text-neutral-800">{sec.uid}</span>
                <span className="text-sm text-neutral-500 ml-2">{sec.name}</span>
                {sec.sector && <span className="text-xs text-neutral-400 ml-2">{sec.sector}</span>}
              </button>
            ))}
          </div>
        )}

        {/* Workflow picker */}
        <label className="block text-sm font-medium text-neutral-700 mt-4 mb-1">Workflow</label>
        <select
          value={workflowId}
          onChange={e => setWorkflowId(e.target.value)}
          className="w-full h-9 px-2 text-sm bg-surface border border-neutral-200 rounded-lg"
        >
          {workflows.length === 0 && <option value="">No published workflows available</option>}
          {workflows.map(w => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>

        {/* Optional event date */}
        <label className="block text-sm font-medium text-neutral-700 mt-4 mb-1">
          Event date <span className="font-normal text-neutral-400">(optional — defaults to the next earnings date)</span>
        </label>
        <input
          type="date"
          value={eventDate}
          onChange={e => setEventDate(e.target.value)}
          className="w-full h-9 px-3 text-sm bg-surface border border-neutral-200 rounded-lg"
        />

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-warning-50 border border-warning-200 text-sm text-warning-800">
            {error}
            {existingId && (
              <button
                type="button"
                className="block mt-2 font-medium text-primary-600 hover:text-primary-700"
                onClick={() => {
                  onClose();
                  navigate(`/task/${existingId}`);
                }}
              >
                Resume existing workup →
              </button>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Starting…' : 'Start Workup'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickStartModal;
